import { Link } from "react-router-dom";
import { IMG, SITE } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import { Phone, Mail } from "lucide-react";

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Brand crest */}
      <section className="container-edge pt-24 pb-12 text-center">
        <p className="overline">The House of Excellence</p>
        <div className="mt-6">
          <p
            className="text-5xl md:text-7xl lg:text-8xl tracking-tight"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            Champagne
          </p>
          <div className="flex items-center justify-center gap-4 my-3">
            <span className="h-px w-16 md:w-24 bg-sand" />
            <span
              className="text-3xl md:text-4xl text-sand"
              style={{ fontFamily: "Playfair Display, serif", fontStyle: "italic" }}
            >
              &amp;
            </span>
            <span className="h-px w-16 md:w-24 bg-sand" />
          </div>
          <p
            className="text-5xl md:text-7xl lg:text-8xl tracking-tight text-sand"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            Sunshine
          </p>
          <p className="mt-6 text-xl md:text-2xl italic text-ocean" style={{ fontFamily: "Playfair Display, serif" }}>
            "Celebrate every move."
          </p>
          <p className="mt-4 overline">
            Local Experts · Skilled Negotiators · Trusted Advisors
          </p>
        </div>
        <p className="mt-10 text-soft max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
          Guiding you through one of the largest and best investments you can make — with the
          celebration of a milestone worth toasting, and the warmth to guide you there.
        </p>
      </section>

      {/* Team hero photo — full bleed on brand teal */}
      <section
        className="relative"
        style={{ backgroundColor: "#1e4d5d" }}
        data-testid="team-photo-section"
      >
        <div className="container-edge py-16 md:py-24">
          <div className="max-w-5xl mx-auto">
            <img
              src={IMG.team}
              alt="Champagne Mora and Sunshine Rolland"
              className="w-full h-auto"
            />
            <div className="mt-10 grid grid-cols-2 gap-6 md:gap-12 text-center">
              <div>
                <p
                  className="text-2xl md:text-4xl text-sand"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Sonia &ldquo;Sunshine&rdquo; Rolland
                </p>
                <p className="mt-2 text-[11px] tracking-[0.28em] uppercase text-white/80">
                  Real Estate Agent since 2015
                </p>
              </div>
              <div>
                <p
                  className="text-2xl md:text-4xl text-sand"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Champagne Mora
                </p>
                <p className="mt-2 text-[11px] tracking-[0.28em] uppercase text-white/80">
                  Real Estate Agent, Since 1999
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Sunshine */}
      <section className="section" data-testid="about-sunshine">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <div className="zoom-img bg-linen max-w-[420px] mx-auto lg:mx-0 lg:sticky lg:top-28 overflow-hidden">
              <img
                src={IMG.agent}
                alt="Champagne & Sunshine team"
                className="w-full aspect-[4/3] object-cover img-warm"
                style={{ objectPosition: "center 30%" }}
              />
            </div>
            <div className="mt-6 max-w-[420px] mx-auto lg:mx-0">
              <p className="overline text-sand">Sunshine</p>
              <p className="mt-2 text-2xl" style={{ fontFamily: "Playfair Display, serif" }}>
                Sunshine Rolland
              </p>
              <p className="mt-2 text-sm text-soft">
                Regional Sales Director · West Coast Escrow<br />
                DRE 02096015 · KW L.A. Harbor · KW Luxury
              </p>
              <ul className="mt-4 space-y-2 text-sm">
                <li className="flex items-center gap-3">
                  <Phone size={14} className="text-sand" />
                  <a href={SITE.phoneHref} className="hover:text-sand transition-colors">{SITE.phone}</a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail size={14} className="text-sand" />
                  <a href={SITE.emailHref} className="hover:text-sand transition-colors break-all">{SITE.email}</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="lg:col-span-8 lg:pl-4">
            <p className="overline">Meet Sunshine</p>
            <h2 className="mt-6 text-5xl md:text-6xl" style={{ fontFamily: "Playfair Display, serif" }}>
              Meet Sonia.
            </h2>
            <div className="mt-10 space-y-7 text-soft leading-relaxed text-base md:text-lg max-w-2xl">
              <p>
                Los Angeles isn't just a city — it's a dream, a shimmering mirage of opulence and
                possibility. Its magnetic pull draws in those who search for the pinnacle of luxury
                living, promising panoramic vistas of the Pacific Ocean and estates nestled in the
                hills of Bel Air. For us, this city isn't just a place; it's a feeling etched deep
                within our hearts.
              </p>
              <p>
                Every street tells a story of diversity and vibrancy, resonating with the beating
                heart of a luxury real estate market unlike any other. For us, this industry is
                about weaving unforgettable experiences that echo in the souls of buyers and sellers
                alike. We've dedicated ourselves to making it our life's mission to deliver unparalleled
                service, expertise, and results to our clients.
              </p>
            </div>

            <blockquote
              className="mt-12 pl-8 border-l-2 max-w-2xl"
              style={{ borderColor: "var(--secondary)" }}
              data-testid="about-quote"
            >
              <p
                className="text-2xl md:text-3xl leading-snug"
                style={{ fontFamily: "Playfair Display, serif", fontStyle: "italic", color: "var(--text-primary)" }}
              >
                Most opportunities are disguised as problems.
              </p>
              <footer className="overline mt-6">— Sunshine</footer>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Meet Champagne */}
      <section className="section bg-linen" data-testid="about-champagne">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-8 lg:pr-8 lg:order-1 order-2">
            <p className="overline">Meet Champagne</p>
            <h2 className="mt-6 text-5xl md:text-6xl" style={{ fontFamily: "Playfair Display, serif" }}>
              Meet Champagne.
            </h2>
            <div className="mt-10 space-y-7 text-soft leading-relaxed text-base md:text-lg max-w-2xl">
              <p>
                In the sprawling landscape of Los Angeles, we don't just sell and buy homes; we
                facilitate dreams, foster connections, and build legacies. At the heart of our
                approach is a deep appreciation for the role families play in our lives and the
                importance of multi-generational wealth building.
              </p>
              <p className="italic" style={{ fontFamily: "Playfair Display, serif" }}>
                En el extenso paisaje de Los Ángeles, no solo vendemos y compramos casas;
                facilitamos sueños, fomentamos conexiones y construimos legados. En el centro de
                nuestro enfoque se encuentra una profunda apreciación por el papel que las
                familias desempeñan en nuestras vidas y la importancia de la construcción de
                riqueza multi-generacional.
              </p>
            </div>

            <blockquote
              className="mt-12 pl-8 border-l-2 max-w-2xl"
              style={{ borderColor: "var(--secondary)" }}
            >
              <p
                className="text-2xl md:text-3xl leading-snug"
                style={{ fontFamily: "Playfair Display, serif", fontStyle: "italic", color: "var(--text-primary)" }}
              >
                What it takes to be an exceptional real estate agent is love and understanding
                for your community.
              </p>
              <footer className="overline mt-6">— Champagne</footer>
            </blockquote>
          </div>

          <div className="lg:col-span-4 lg:order-2 order-1">
            <div className="bg-paper max-w-[420px] mx-auto lg:mx-0 lg:sticky lg:top-28 overflow-hidden">
              <img
                src={IMG.team}
                alt="Champagne & Sunshine team"
                className="w-full aspect-[4/3] object-cover img-warm"
                style={{ objectPosition: "center 30%" }}
              />
            </div>
            <div className="mt-6 max-w-[420px] mx-auto lg:mx-0">
              <p className="overline text-sand">Champagne</p>
              <p className="mt-2 text-2xl" style={{ fontFamily: "Playfair Display, serif" }}>
                {SITE.partner}
              </p>
              <p className="mt-2 text-sm text-soft">
                Luxury Real Estate<br />
                {SITE.partnerDre} · KW L.A. Harbor · KW Luxury
              </p>
              <ul className="mt-4 space-y-2 text-sm">
                <li className="flex items-center gap-3">
                  <Phone size={14} className="text-sand" />
                  <a href={SITE.partnerPhoneHref} className="hover:text-sand transition-colors">{SITE.partnerPhone}</a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail size={14} className="text-sand" />
                  <a href={SITE.partnerEmailHref} className="hover:text-sand transition-colors break-all">{SITE.partnerEmail}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Support team */}
      <section className="section" data-testid="about-support">
        <div className="max-w-3xl">
          <p className="overline">Our sales team is your team</p>
          <h2 className="mt-4 text-4xl md:text-5xl" style={{ fontFamily: "Playfair Display, serif" }}>
            You're never doing this alone.
          </h2>
          <p className="mt-6 text-soft leading-relaxed">
            Behind every transaction is a hand-picked bench of specialists — vetted by us and
            trusted by our clients for years.
          </p>
        </div>
        <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {[
            { role: "Transaction Coordinator", name: "Claudia Romero" },
            { role: "Marketing", name: "MJ Mina" },
            { role: "Lender", name: "Jen Zavat" },
            { role: "Inspections", name: "Adrianna Parra" },
            { role: "Title Company", name: "First American" },
            { role: "Escrow", name: "West Coast Escrow" },
          ].map((m, i) => (
            <div key={m.role} className="border border-soft p-6" data-testid={`support-${i}`}>
              <p className="overline">{m.role}</p>
              <p className="mt-3 text-lg leading-snug" style={{ fontFamily: "Playfair Display, serif" }}>{m.name}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Work with the team CTA */}
      <section className="section" data-testid="about-cta">
        <div className="text-center max-w-3xl mx-auto">
          <p className="overline">Together</p>
          <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl" style={{ fontFamily: "Playfair Display, serif" }}>
            Two agents. One phone number. Every step of the way.
          </h2>
          <p className="mt-6 text-soft max-w-xl mx-auto">
            You're never juggling two point people. Whoever you talk to first knows everything
            about your file — and both of us have your back.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-primary" data-testid="about-contact-btn">
              Work with the team
            </Link>
            <a href={SITE.teamEmailHref} className="btn-outline" data-testid="about-email-btn">
              {SITE.teamEmail}
            </a>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="section bg-linen">
        <SectionHeader overline="Credentials" title="The receipts." />
        <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-8">
          {[
            { t: "Champagne", d: "DRE 01271524" },
            { t: "Sunshine", d: "DRE 02096015" },
            { t: "Brokerage", d: "KW L.A. Harbor" },
            { t: "Also", d: "KW Luxury" },
            { t: "Service area", d: "Greater LA" },
          ].map((c, i) => (
            <div key={c.t} data-testid={`credential-${i}`}>
              <p className="overline">{c.t}</p>
              <p className="mt-3 text-lg" style={{ fontFamily: "Playfair Display, serif" }}>{c.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 text-xs text-soft uppercase tracking-[0.18em]">
          Each office is independently owned and operated
        </p>
      </section>
    </div>
  );
}
